// useCouncilHistory.js — Council v2 history sidebar data layer.
//
// Deep hook over GET /api/council/history (cursor paginated) + GET /api/council/:id.
// Owns: the page list, the opaque next_cursor, loading/error state, and reuse
// (fetching a detail row and normalizing it into the render shape). All reads go
// through apiFetch which attaches the bearer token and is scoped by the server to
// the authenticated user — the UI never constructs ownership.

import { useCallback, useEffect, useRef, useState } from 'react';
import { apiFetch } from '../../lib/api.js';
import { normalizeHistoryDetail } from './councilExport.js';

const PAGE_LIMIT = 20;

export function useCouncilHistory() {
  const [items, setItems] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [openDetail, setOpenDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const fetchPage = useCallback(async (cursor, { append }) => {
    const qs = new URLSearchParams({ limit: String(PAGE_LIMIT) });
    // The cursor is opaque; pass it through untouched (server validates).
    if (cursor) qs.set('cursor', cursor);
    const res = await apiFetch(`/api/council/history?${qs.toString()}`);
    if (!res.ok) {
      setError(res.error || `Falha ao carregar histórico (${res.status})`);
      return;
    }
    const pageItems = Array.isArray(res.data?.items) ? res.data.items : [];
    setItems((prev) => (append ? [...prev, ...pageItems] : pageItems));
    setNextCursor(res.data?.next_cursor || null);
  }, []);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      await fetchPage(null, { append: false });
    } finally {
      setLoading(false);
    }
  }, [fetchPage]);

  const loadMore = useCallback(async () => {
    if (!nextCursor || loadingMore) return;
    setLoadingMore(true);
    try {
      await fetchPage(nextCursor, { append: true });
    } finally {
      setLoadingMore(false);
    }
  }, [nextCursor, loadingMore, fetchPage]);

  // Load on mount.
  const ranRef = useRef(false);
  useEffect(() => {
    if (ranRef.current) return undefined;
    ranRef.current = true;
    refresh();
    return undefined;
  }, [refresh]);

  const openDeliberation = useCallback(async (id) => {
    if (!id) return null;
    setDetailLoading(true);
    setError('');
    try {
      const res = await apiFetch(`/api/council/${encodeURIComponent(id)}`);
      if (!res.ok) {
        setError(res.error || `Falha ao abrir deliberação (${res.status})`);
        return null;
      }
      const normalized = normalizeHistoryDetail(res.data || {});
      setOpenDetail(normalized);
      // Return the row so callers (reuse) do not depend on async state settling.
      return normalized;
    } finally {
      setDetailLoading(false);
    }
  }, []);

  const closeDetail = useCallback(() => setOpenDetail(null), []);

  return {
    items,
    nextCursor,
    hasMore: !!nextCursor,
    loading,
    loadingMore,
    error,
    openDetail,
    detailLoading,
    refresh,
    loadMore,
    openDeliberation,
    closeDetail,
  };
}
